import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Avatar,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import logger from '../../utils/logger';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '1073846153729-dummygoogleclientidfortesting.apps.googleusercontent.com';

const Navbar = () => {
  const { user, logout, googleLogin } = useAuth();
  const { darkMode, toggleDarkMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [authError, setAuthError] = useState('');
  const gsiReady = useRef(false);

  // ─── Handle Google credential callback ──────────────────────────────
  const handleGoogleResponse = useCallback(async (response) => {
    logger.info('Google credential received, authenticating...');
    const result = await googleLogin(response.credential);
    if (result.success) {
      logger.info('Authentication successful');
      navigate('/dashboard');
    } else {
      logger.error('Authentication failed: ' + result.error);
      setAuthError(result.error || 'Sign-in failed. Please try again.');
    }
  }, [googleLogin, navigate]);

  // ─── Load & initialise Google Identity Services once ────────────────
  const initGSI = useCallback(() => {
    try {
      /* global google */
      if (typeof google !== 'undefined' && google.accounts?.id) {
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        gsiReady.current = true;
        logger.info('Google GSI initialized');
      }
    } catch (err) {
      logger.error('GSI init failed', err);
    }
  }, [handleGoogleResponse]);

  useEffect(() => {
    if (document.getElementById('google-gsi-script')) {
      // Script already exists — try initializing immediately
      initGSI();
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      logger.info('Google GSI script loaded');
      initGSI();
    };
    script.onerror = () => {
      logger.error('Failed to load Google GSI script');
    };
    document.head.appendChild(script);
  }, [initGSI]);

  // Re-initialize when handleGoogleResponse changes (e.g. user logs out)
  useEffect(() => {
    if (gsiReady.current) {
      initGSI();
    }
  }, [initGSI]);

  // ─── Listen for 'openLoginModal' (token expiry etc.) ────────────────
  useEffect(() => {
    const handler = () => triggerGoogleSignIn();
    window.addEventListener('openLoginModal', handler);
    return () => window.removeEventListener('openLoginModal', handler);
  }, []);

  // ─── Trigger Google account picker directly ─────────────────────────
  const triggerGoogleSignIn = () => {
    try {
      /* global google */
      if (typeof google !== 'undefined' && google.accounts?.id) {
        // prompt() shows the native Google One-Tap / account picker popup
        google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Fallback: open Google OAuth popup via a rendered hidden button click
            openGooglePopupFallback();
          }
        });
      } else {
        openGooglePopupFallback();
      }
    } catch (err) {
      logger.error('Could not trigger Google sign-in', err);
      openGooglePopupFallback();
    }
  };

  // Fallback: programmatically click a hidden Google Sign-In button
  const openGooglePopupFallback = () => {
    const btn = document.getElementById('hidden-google-btn');
    if (btn) {
      const inner = btn.querySelector('div[role="button"]') || btn.firstChild;
      if (inner) inner.click();
      else btn.click();
    } else {
      setAuthError('Google Sign-In unavailable. Please check your internet connection.');
    }
  };

  // ─── Render hidden Google button (fallback trigger) ──────────────────
  useEffect(() => {
    const renderHiddenBtn = () => {
      try {
        /* global google */
        if (typeof google !== 'undefined' && google.accounts?.id) {
          const el = document.getElementById('hidden-google-btn');
          if (el && !el.hasChildNodes()) {
            google.accounts.id.renderButton(el, {
              type: 'standard',
              theme: 'filled_blue',
              size: 'large',
              text: 'signin_with',
            });
          }
        }
      } catch (e) {}
    };
    const t = setTimeout(renderHiddenBtn, 500);
    return () => clearTimeout(t);
  }, [user]);

  // Dev-mode simulate
  const handleSimulateLogin = async () => {
    const testEmail = window.prompt('Enter mock Google email:', 'testclient@gmail.com');
    if (!testEmail) return;
    const testName = window.prompt('Enter mock name:', 'Test Client');
    if (!testName) return;
    try {
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify({
        email: testEmail.trim().toLowerCase(),
        name: testName.trim(),
        picture: 'https://lh3.googleusercontent.com/a/default-user',
        email_verified: true,
        aud: GOOGLE_CLIENT_ID,
      }));
      await handleGoogleResponse({ credential: `mock_${header}.${payload}.signature` });
    } catch (err) {
      setAuthError('Mock login failed: ' + err.message);
    }
  };

  // ─── Existing handlers ───────────────────────────────────────────────
  const handleProfileMenuOpen = (e) => setProfileAnchorEl(e.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchorEl(null);
  const isAdminPage = location.pathname.startsWith('/admin') && location.pathname !== '/admin-login';
  const handleToggleSidebar = () => window.dispatchEvent(new CustomEvent('toggleAdminSidebar'));
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); navigate('/'); handleMenuClose(); handleProfileMenuClose(); };

  const navItems = [];

  return (
    <>
      {/* Hidden Google button used as popup fallback */}
      <Box
        id="hidden-google-btn"
        sx={{ position: 'fixed', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none', zIndex: -1 }}
      />

      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#5c1c73', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAdminPage && (
              <IconButton color="inherit" aria-label="toggle sidebar" onClick={handleToggleSidebar} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            {isMobile && (
              <Tooltip title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                <IconButton onClick={toggleDarkMode} color="inherit">
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="h5" component={Link} to="/" sx={{ fontWeight: 800, textDecoration: 'none', color: 'inherit', ml: (isAdminPage || isMobile) ? 0 : 4 }}>
              EVENZA
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {navItems.map((item) => (
                <Button key={item.path} component={Link} to={item.path} color="inherit" sx={{ textTransform: 'none' }}>
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isMobile && (
              <Tooltip title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                <IconButton onClick={toggleDarkMode} color="inherit">
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
            )}

            {user ? (
              <>
                <Tooltip title={user.name || 'Account'}>
                  <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
                    <Avatar
                      alt={user.name}
                      src={user.picture || ''}
                      sx={{ width: 40, height: 40, border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                    >
                      {user.name ? user.name[0].toUpperCase() : 'U'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={profileAnchorEl}
                  open={Boolean(profileAnchorEl)}
                  onClose={handleProfileMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      mt: 1.5, minWidth: 200, borderRadius: 2, overflow: 'visible',
                      '&::before': { content: '""', display: 'block', position: 'absolute', top: 0, right: 14, width: 10, height: 10, bgcolor: 'background.paper', transform: 'translateY(-50%) rotate(45deg)', zIndex: 0 },
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography variant="subtitle2" fontWeight={700}>{user.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                  </Box>
                  {user.role === 'admin' && (
                    <MenuItem component={Link} to="/admin" onClick={handleProfileMenuClose} sx={{ fontWeight: 500 }}>Admin Panel</MenuItem>
                  )}
                  <MenuItem component={Link} to="/dashboard" onClick={handleProfileMenuClose} sx={{ fontWeight: 500 }}>Dashboard</MenuItem>
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main', fontWeight: 500 }}>Logout</MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                onClick={triggerGoogleSignIn}
                variant="contained"
                sx={{
                  bgcolor: 'white',
                  color: '#4285F4',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '24px',
                  px: 2.2,
                  py: 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.15)',
                  border: '1px solid #dadce0',
                  '&:hover': { bgcolor: '#f8f9fa', boxShadow: '0 3px 6px 0 rgba(0,0,0,0.2)', borderColor: '#d2d2d2' },
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" style={{ display: 'block' }}>
                  <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.47h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.91c1.7-1.56 2.69-3.87 2.69-6.6z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.2l-2.91-2.26a5.58 5.58 0 0 1-8.52-3.13H.57v2.33A9 9 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.53 10.41a5.41 5.41 0 0 1 0-3.42V4.66H.57a9 9 0 0 0 0 8.08l2.96-2.33z"/>
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35L15 2.4A9 9 0 0 0 .57 4.66l2.96 2.33a5.48 5.48 0 0 1 5.47-3.41z"/>
                </svg>
                Sign in
              </Button>
            )}

            {/* Dev simulate button */}
            {!user && process.env.NODE_ENV !== 'production' && (
              <Button
                onClick={handleSimulateLogin}
                variant="text"
                size="small"
                sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontSize: '0.7rem', display: { xs: 'none', md: 'flex' } }}
              >
                [Dev]
              </Button>
            )}

            {isMobile && navItems.length > 0 && (
              <>
                <IconButton color="inherit" onClick={handleMenuOpen}><MenuIcon /></IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  {navItems.map((item) => (
                    <MenuItem key={item.path} component={Link} to={item.path} onClick={handleMenuClose}>{item.label}</MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Auth error snackbar */}
      <Snackbar
        open={Boolean(authError)}
        autoHideDuration={5000}
        onClose={() => setAuthError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setAuthError('')} sx={{ borderRadius: 2 }}>
          {authError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Navbar;
